import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { UserService } from '@duck/api-definitions'
import { InvitationTableFilters } from './InvitationTableFilters'
import { InvitationsTable } from './InvitationsTable'
import { InvitationDialog } from './InvitationDialog'
import { baseColumns, type Column } from './utils'
import { apis } from '../../modules/apiClients'
import { useTar } from '@/modules'
import { useAdminInvitations } from '@/hooks/useAdminInvitations'

const STORAGE_KEY = 'admin-invitation-filters'

interface FilterState {
  sortField: keyof UserService.Invitation
  sortDirection: 'asc' | 'desc'
  filterText: string
  statusFilter: string[]
  organizationIdFilter: string[]
  visibleColumnKeys: string[]
}

// Utility functions for localStorage
const loadFiltersFromStorage = (): FilterState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const saveFiltersToStorage = (filters: FilterState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // Ignore localStorage errors
  }
}

const reconstructColumns = (visibleKeys?: string[]): Column[] => {
  const keys = visibleKeys || baseColumns.filter(col => col.visible).map(col => col.key)
  return baseColumns.map(col => ({
    ...col,
    visible: keys.includes(col.key)
  }))
}

export const InvitationsManagement: React.FC = () => {
  const { t } = useTar()
  // Initialize state from localStorage or defaults
  const storedFilters = loadFiltersFromStorage()

  const [sortField, setSortField] = useState<keyof UserService.Invitation>(
    storedFilters?.sortField ?? 'createdAt'
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    storedFilters?.sortDirection ?? 'desc'
  )
  const [filterText, setFilterText] = useState(storedFilters?.filterText ?? '')
  const [statusFilter, setStatusFilter] = useState<string[]>(
    Array.isArray(storedFilters?.statusFilter)
      ? (storedFilters?.statusFilter as string[])
      : (storedFilters?.statusFilter ? [storedFilters?.statusFilter as unknown as string] : [])
  )
  const [organizationIdFilter, setOrganizationIdFilter] = useState<string[]>(
    Array.isArray(storedFilters?.organizationIdFilter)
      ? (storedFilters?.organizationIdFilter as string[])
      : []
  )
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const [columns, setColumns] = useState<Column[]>(
    () => reconstructColumns(storedFilters?.visibleColumnKeys)
  )

  // Dialog state
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false)
  const [selectedInvitation, setSelectedInvitation] = useState<UserService.Invitation | null>(null)

  // Save to localStorage whenever filter state changes
  useEffect(() => {
    const visibleColumnKeys = columns.filter(col => col.visible).map(col => col.key)
    const filterState: FilterState = {
      sortField,
      sortDirection,
      filterText,
      statusFilter,
      organizationIdFilter,
      visibleColumnKeys,
    }
    saveFiltersToStorage(filterState)
  }, [sortField, sortDirection, filterText, statusFilter, organizationIdFilter, columns])

  // Map UI filters to backend query filters
  const queryFilters = useMemo(() => {
    const filters: {
      status?: string
      organizationId?: string
    } = {}

    if (statusFilter.length === 1) {
      filters.status = statusFilter[0]
    }

    if (organizationIdFilter.length === 1) {
      filters.organizationId = organizationIdFilter[0]
    }

    return filters
  }, [statusFilter, organizationIdFilter])

  const {
    invitations,
    hasMore,
    isInitialLoading,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    refetch,
  } = useAdminInvitations({
    queryFilters,
    sortField,
    sortDirection,
  })

  // Extract unique access groups from invitations
  const allAccessGroups = useMemo(() =>
    Array.from(
      new Set(invitations.flatMap(invitation => invitation.accessGroups || []))
    ).sort(),
    [invitations]
  )

  const filteredInvitations = useMemo(() => {
    let filtered = invitations

    // Text filter (client-side for now)
    if (filterText) {
      const searchTerm = filterText.toLowerCase()
      filtered = filtered.filter(invitation =>
        invitation.email.toLowerCase().includes(searchTerm) ||
        (invitation.firstName || '').toLowerCase().includes(searchTerm) ||
        (invitation.lastName || '').toLowerCase().includes(searchTerm)
      )
    }

    // Status filter (client-side when multi-select)
    if (statusFilter.length > 1) {
      const statusSet = new Set(statusFilter)
      filtered = filtered.filter(invitation => statusSet.has(invitation.status as string))
    }

    // Organization filter (client-side for now)
    if (organizationIdFilter.length > 0) {
      const orgSet = new Set(organizationIdFilter)
      filtered = filtered.filter(invitation =>
        invitation.organizationId && orgSet.has(invitation.organizationId)
      )
    }

    return filtered
  }, [invitations, filterText, statusFilter, organizationIdFilter])

  const handleLoadMorePage = useCallback(() => {
    if (!hasMore) return
    void fetchNextPage()
  }, [hasMore, fetchNextPage])

  const handleSort = (field: keyof UserService.Invitation) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const toggleColumnVisibility = (columnKey: string) => {
    setColumns(prev => prev.map(col =>
      col.key === columnKey
        ? { ...col, visible: !col.visible }
        : col
    ))
  }

  const handleCreateNewInvitation = () => {
    setSelectedInvitation(null)
    setIsInvitationDialogOpen(true)
  }

  const handleInvitationClick = (invitation: UserService.Invitation) => {
    setSelectedInvitation(invitation)
    setIsInvitationDialogOpen(true)
  }

  const [prefilledInvitationData, setPrefilledInvitationData] = useState<Partial<UserService.Invitation> | undefined>(undefined)

  const handleInviteClick = (invitation: UserService.Invitation) => {
    // Set prefilled data for a new invitation
    setPrefilledInvitationData({
      email: invitation.email,
      firstName: invitation.firstName,
      lastName: invitation.lastName,
    })
    setSelectedInvitation(null)
    setIsInvitationDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsInvitationDialogOpen(false)
    setSelectedInvitation(null)
    setPrefilledInvitationData(undefined)
  }

  const visibleColumns = columns.filter(col => col.visible)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
            {t('invitations.invitationManagement')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('invitations.invitationManagementDescription')}
          </p>
        </div>
        <button
          onClick={handleCreateNewInvitation}
          className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('invitations.sendInvitation')}
        </button>
      </div>

      {/* Filters and Controls */}
      <InvitationTableFilters
        filterText={filterText}
        setFilterText={setFilterText}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        organizationIdFilter={organizationIdFilter}
        setOrganizationIdFilter={setOrganizationIdFilter}
        columns={columns}
        columnDropdownOpen={columnDropdownOpen}
        setColumnDropdownOpen={setColumnDropdownOpen}
        toggleColumnVisibility={toggleColumnVisibility}
      />

      {/* Error State */}
      {isError && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">
            {t('common.error')}
          </div>
        </div>
      )}

      {/* Invitations Table */}
      {!isError && (
        <InvitationsTable
          invitations={filteredInvitations}
          totalInvitations={invitations.length}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onInvitationClick={handleInvitationClick}
          onInviteClick={handleInviteClick}
          loadingMode={isInitialLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMorePage}
          isPageLoading={isFetchingNextPage}
        />
      )}

      {/* Invitation Dialog */}
      <InvitationDialog
        isOpen={isInvitationDialogOpen}
        onClose={handleCloseDialog}
        invitation={selectedInvitation}
        prefilledData={prefilledInvitationData}
        onInvitationSaved={() => { void refetch() }}
        allAccessGroups={allAccessGroups}
      />
    </div>
  )
}
